import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import ErrorState from '@/components/ErrorState';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Send } from 'lucide-react';
import { ReportButton } from '@/components/ReportButton';
import NotificationBell from '@/components/NotificationBell';
import { toast } from 'sonner';
import { divinationLabel } from '@/data/divination';
import { reqNo } from '@/data/format';
import { birthDateText, birthTimeText } from '@/data/birth';
import { symbolOf } from '@/data/money';

const Chat = () => {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesRef = useRef([]);
  const pollingRef = useRef(false);
  const sendingRef = useRef(false);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const fetchOrderAndMessages = useCallback(async () => {
    try {
      const [orderRes, messagesRes] = await Promise.all([
        axios.get(`${API}/orders/${orderId}`, { withCredentials: true }),
        axios.get(`${API}/orders/${orderId}/messages`, { withCredentials: true })
      ]);
      setOrder(orderRes.data);
      setMessages(messagesRes.data);
      setHasOlder(messagesRes.data.length === 100);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('無法載入訊息');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const pollMessages = useCallback(async () => {
    if (pollingRef.current || sendingRef.current) return;
    pollingRef.current = true;
    try {
      const oldest = messagesRef.current[0];
      let before;
      let rows = [];
      let page;
      // Refresh the loaded history as well: moderation can change existing messages.
      do {
        const response = await axios.get(`${API}/orders/${orderId}/messages`, {
          withCredentials: true, params: before ? { before } : {}
        });
        page = response.data;
        rows = [...page, ...rows];
        before = page[0]?.message_id;
      } while (page.length === 100 && oldest && !rows.some(m => m.message_id === oldest.message_id));
      setMessages(prev => {
        const byId = new Map(prev.map(m => [m.message_id, m]));
        rows.forEach(m => byId.set(m.message_id, m));
        return [...byId.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.message_id.localeCompare(b.message_id));
      });
    } catch (error) {
      // Silent fail on background polling
      console.error('Polling error:', error);
    } finally {
      pollingRef.current = false;
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderAndMessages();
  }, [fetchOrderAndMessages]);

  // Poll for new messages every 3 seconds
  useEffect(() => {
    if (!orderId) return;
    const intervalId = setInterval(() => {
      pollMessages();
    }, 3000);
    return () => clearInterval(intervalId);
  }, [orderId, pollMessages]);

  const latestMessageId = messages[messages.length - 1]?.message_id;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [latestMessageId]);

  const loadOlder = async () => {
    if (loadingOlder || !messages.length) return;
    setLoadingOlder(true);
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/messages`, {
        withCredentials: true, params: { before: messages[0].message_id }
      });
      setMessages(prev => {
        const byId = new Map([...data, ...prev].map(m => [m.message_id, m]));
        return [...byId.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.message_id.localeCompare(b.message_id));
      });
      setHasOlder(data.length === 100);
    } catch (e) { toast.error('無法載入較早訊息'); }
    finally { setLoadingOlder(false); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingRef.current) return;

    sendingRef.current = true;
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/orders/${orderId}/messages`,
        { content: newMessage },
        { withCredentials: true }
      );
      setMessages(prev => prev.some(m => m.message_id === response.data.message_id) ? prev : [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('傳送失敗');
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">載入中...</p>
      </div>
    );
  }

  if (!order) {
    return <ErrorState message="找不到訂單" />;
  }

  const isClient = user.role === 'client';
  const isAdmin = user.role === 'admin';
  const dashboardPath = isAdmin ? '/admin/dashboard' : isClient ? '/client/dashboard' : '/astrologer/dashboard';
  const otherPartyId = isClient ? order.astrologer_id : order.client_id;
  const ri = order.request_info || {};

  const moderateMessage = async (messageId, hidden) => {
    try {
      await axios.post(`${API}/admin/messages/${messageId}/moderate`, { hidden }, { withCredentials: true });
      setMessages((prev) => prev.map((m) => (m.message_id === messageId ? { ...m, hidden } : m)));
      toast.success(hidden ? '已隱藏訊息' : '已還原訊息');
    } catch (e) {
      toast.error('操作失敗');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={dashboardPath}>
            <Button variant="ghost" size="sm" className="rounded-sm" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </Link>
          <div className="min-w-0">
            <h2 className="text-lg font-serif text-[#1A1A1A] truncate" data-testid="chat-title">
              {order.other_party_name || '私訊'}
              {ri.seq && <span className="ml-2 text-xs text-[#9C9C9C] font-mono font-sans align-middle">{reqNo(ri.seq)}</span>}
            </h2>
            <p className="text-xs text-[#5C5C5C] truncate" data-testid="chat-context">
              {divinationLabel(ri)}
              {!isClient && ri.client_name ? ` · ${ri.client_name}：${birthDateText(ri)} ${birthTimeText(ri)}` : ''}
              {order.amount ? ` · ${symbolOf(order.currency)} ${Number(order.amount).toLocaleString()}` : ''}
              {order.consultation_minutes ? ` · ${order.consultation_minutes} 分鐘` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isClient && (
            <Link to={`/client/request/${order.request_id}`} className="text-xs text-[#C93B2B] hover:underline" data-testid="view-request-link">
              查看求測單
            </Link>
          )}
          <NotificationBell />
          <ReportButton targetType="user" targetId={otherPartyId} label="舉報對方" testId="report-other-party" />
        </div>
      </nav>

      <div className="bg-[#FEF8F0] border-b border-[#C93B2B] px-6 md:px-12 lg:px-24 py-3">
        <p className="text-sm text-[#1A1A1A]">
          {isAdmin
            ? '🛡️ 管理員檢視模式 — 只可閱讀同隱藏訊息，唔可以發言。'
            : '⚠️ 請勿在聊天中分享個人聯絡資訊。所有溝通請在平台內進行。'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-4xl mx-auto space-y-4">
          {hasOlder && <Button variant="ghost" onClick={loadOlder} disabled={loadingOlder} data-testid="load-older-messages">{loadingOlder ? '載入中…' : '載入較早訊息'}</Button>}
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-[#5C5C5C]">尚無訊息，開始你們的對話吧！</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMyMessage = msg.sender_id === user.user_id;
              return (
                <div
                  key={msg.message_id}
                  className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'} group`}
                  data-testid={`message-${msg.message_id}`}
                >
                  <div className={`max-w-xl ${
                    isMyMessage 
                      ? 'bg-[#C93B2B] text-white' 
                      : 'bg-white border border-[#E5E0D8] text-[#1A1A1A]'
                  } rounded-sm p-4`}>
                    <p className="text-xs mb-1 opacity-75">{isMyMessage ? '你' : msg.sender_name}</p>
                    {msg.hidden && isAdmin && (
                      <p className="text-xs text-[#C93B2B] mb-1">（已隱藏 — 用戶只見到佔位文字）</p>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => moderateMessage(msg.message_id, !msg.hidden)}
                        className="text-xs underline opacity-75 hover:opacity-100 mt-1"
                        data-testid={`moderate-${msg.message_id}`}
                      >
                        {msg.hidden ? '還原訊息' : '隱藏訊息'}
                      </button>
                    )}
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs opacity-75">
                        {new Date(msg.created_at).toLocaleString('zh-TW')}
                      </p>
                      {!isMyMessage && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                          <ReportButton
                            targetType="message"
                            targetId={msg.message_id}
                            label="舉報"
                            testId={`report-message-${msg.message_id}`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {!isAdmin && (
      <div className="bg-white border-t border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4">
        <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex gap-3 items-end">
          <Textarea
            maxLength={5000}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            placeholder="輸入訊息…（Enter 傳送，Shift+Enter 換行）"
            className="flex-1 rounded-sm resize-none min-h-[42px] max-h-40"
            rows={2}
            disabled={sending}
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </form>
      </div>
      )}
    </div>
  );
};

export default Chat;
